import {Box} from '@mui/material';
import React, {useRef, useState} from 'react';
import './CreateJobsFieldsWrapper.scss';

interface TextAreaResizeHandleGripProps {
  displayHandle?: boolean;
  handleMouseDown?: (e: React.PointerEvent<HTMLElement>) => void;
  resizable?: boolean;
}

interface ICreateJobsFieldsWrapper extends TextAreaResizeHandleGripProps {
  header: React.ReactElement | null;
  className?: string;
  children?: React.ReactNode;
  childrenClassName?: string;
  ariaLabel?: string;
  tabIndex?: number;
  MAX_HEIGHT?: number;
  MIN_HEIGHT?: number;
  IsDefaultValueMinHeight?: boolean;
}

const CreateJobsFieldsWrapper = ({
  header,
  className,
  children,
  childrenClassName,
  displayHandle = false,
  resizable = false,
  ariaLabel,
  tabIndex,
  MAX_HEIGHT = 256,
  MIN_HEIGHT = 256,
  IsDefaultValueMinHeight = true,
}: ICreateJobsFieldsWrapper) => {
  const [height, setHeight] = useState<number | null>(null);
  const isResized = useRef(false);

  const wrapperRef = useRef<HTMLTextAreaElement | null>(null);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const handleMouseDown = (e: React.PointerEvent<HTMLElement>) => {
    e.preventDefault();
    startYRef.current = e.clientY;
    startHeightRef.current = wrapperRef.current?.offsetHeight || MIN_HEIGHT;
    document.addEventListener('pointermove', handleMouseMove);
    document.addEventListener('pointerup', handleMouseUp);
  };

  const handleMouseMove = (e: PointerEvent) => {
    const newHeight = startHeightRef.current + (e.clientY - startYRef.current);
    const clampedHeight = Math.min(Math.max(newHeight, MIN_HEIGHT), MAX_HEIGHT);
    setHeight(clampedHeight);
    isResized.current = true;
  };

  const handleMouseUp = () => {
    document.removeEventListener('pointermove', handleMouseMove);
    document.removeEventListener('pointermove', handleMouseUp);
  };
  return (
    <Box
      className={`container-wrapper ${isResized.current ? 'user-resized' : ''}  ${className}`}
      sx={{
        maxHeight: MAX_HEIGHT,
        ...(height
          ? {height: height}
          : IsDefaultValueMinHeight
            ? {minHeight: MIN_HEIGHT}
            : {height: MAX_HEIGHT}),
      }}
      ref={wrapperRef}
    >
      {header && (
        <Box
          className="container-header"
          tabIndex={tabIndex ?? -1}
          aria-label={ariaLabel}
        >
          {header}
        </Box>
      )}
      <Box className={`container-children ${childrenClassName}`}>
        {children}
      </Box>
      <TextAreaResizeHandleGrip
        displayHandle={displayHandle}
        resizable={resizable}
        handleMouseDown={handleMouseDown}
      />
    </Box>
  );
};

export default CreateJobsFieldsWrapper;

const TextAreaResizeHandleGrip = ({
  displayHandle = false,
  resizable = false,
  handleMouseDown,
}: TextAreaResizeHandleGripProps) =>
  resizable && displayHandle ? (
    <Box
      className="textarea-resize-handle"
      onPointerDown={handleMouseDown ?? undefined}
    />
  ) : null;
